// BOORO: dressed ghosts.
import { parseGhostAvatar } from "@booro/ghost-avatar";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type { BotAvatarSet } from "@rakazo/contracts";
import {
  BotAvatar,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  defaultBotAvatarValue,
} from "@rakazo/ui-web";
import { Check, Pencil, Upload, X } from "lucide-react";
import type { ClipboardEvent, DragEvent } from "react";
import { useRef, useState } from "react";
import { GhostWardrobe } from "./ghost-wardrobe";

/**
 * BOORO: the look of a new bot. Every look is a dressed ghost, so the icon sets of Rakazo are not
 * offered; the props stay those of Rakazo's picker.
 */
export function BotLookPicker({
  value,
  identity,
  onChange,
}: {
  value: string;
  identity?: string;
  organicIdentities?: readonly string[];
  avatarSet?: BotAvatarSet;
  onAvatarSetChange?: (set: BotAvatarSet, value: string) => void;
  onChange: (value: string, identity?: string) => void;
}) {
  return <GhostWardrobe value={value} identity={identity} onChange={(next) => onChange(next)} />;
}

export interface AvatarStudioPopoverProps {
  value: string;
  identity?: string;
  // BOORO: ghosts have no icon sets; Rakazo callers still pass one.
  avatarSet?: BotAvatarSet;
  status?: string;
  size?: number;
  onChange: (value: string, avatarSet?: BotAvatarSet) => void;
  disabled?: boolean;
}

export function AvatarStudioPopover({
  value,
  identity,
  status,
  size = 72,
  onChange,
  disabled = false,
}: AvatarStudioPopoverProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"bot" | "upload">("bot");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function restoreDefaultAvatar() {
    // BOORO: a dressed ghost keeps its color and gets its random outfit back.
    const next = parseGhostAvatar(value)?.color ?? defaultBotAvatarValue(value);
    if (next !== value) onChange(next);
  }

  function processImageFile(file: File) {
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const src = event.target?.result as string;
      if (!src) return;
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const targetSize = 256;
        canvas.width = targetSize;
        canvas.height = targetSize;
        if (!ctx) return;

        ctx.beginPath();
        ctx.arc(targetSize / 2, targetSize / 2, targetSize / 2, 0, Math.PI * 2);
        ctx.clip();

        const minDim = Math.min(img.width, img.height);
        const sx = (img.width - minDim) / 2;
        const sy = (img.height - minDim) / 2;
        ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, targetSize, targetSize);

        onChange(canvas.toDataURL("image/webp", 0.9));
        setOpen(false);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  }

  function handleDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) processImageFile(file);
  }

  function handlePaste(event: ClipboardEvent<HTMLButtonElement>) {
    const items = event.clipboardData?.items;
    if (!items) return;
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      if (!item?.type.startsWith("image/")) continue;
      const file = item.getAsFile();
      if (file) {
        processImageFile(file);
        break;
      }
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="group relative cursor-pointer rounded-2xl outline-none transition-transform hover:scale-[1.03] focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={t`Customize bot avatar`}
        data-testid="avatar-studio-trigger"
      >
        <BotAvatar color={value} identity={identity} size={size} status={status} />
        <div className="absolute -right-1 -bottom-1 flex size-6 items-center justify-center rounded-full border-2 border-background bg-secondary text-foreground shadow-md transition-transform group-hover:scale-110">
          <Pencil size={12} strokeWidth={2.2} />
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[90vh] w-[400px] max-w-full gap-4 overflow-y-auto rounded-3xl p-5 sm:max-w-[400px]"
          data-testid="avatar-studio"
        >
          <DialogHeader className="flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-[14px] font-semibold tracking-tight">
              {/* BOORO: Avatar Studio is the ghost wardrobe. */}
              <Trans>Wardrobe</Trans>
            </DialogTitle>
            <DialogDescription className="sr-only">
              <Trans>Dress the ghost or upload an image</Trans>
            </DialogDescription>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t`Close`}
            >
              <X size={16} />
            </button>
          </DialogHeader>

          <div className="flex flex-col items-center justify-center py-2">
            <BotAvatar color={value} identity={identity} size={120} status={status} />
          </div>

          <div className="flex items-center justify-between border-b border-border pb-1">
            <div className="flex items-center rounded-full bg-muted p-1 text-xs">
              <button
                type="button"
                onClick={() => setActiveTab("bot")}
                aria-pressed={activeTab === "bot"}
                className={`rounded-full px-3 py-1 font-medium transition-colors ${
                  activeTab === "bot"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Trans>Ghost</Trans>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("upload")}
                aria-pressed={activeTab === "upload"}
                className={`rounded-full px-3 py-1 font-medium transition-colors ${
                  activeTab === "upload"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Trans>Upload</Trans>
              </button>
            </div>

            <button
              type="button"
              onClick={restoreDefaultAvatar}
              className="px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <Trans>Reset</Trans>
            </button>
          </div>

          {activeTab === "bot" ? (
            // BOORO: the ghost wardrobe replaces the shape and color picker.
            <GhostWardrobe value={value} identity={identity} onChange={onChange} />
          ) : (
            <button
              type="button"
              aria-label={t`Image upload area`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onPaste={handlePaste}
              onClick={() => fileInputRef.current?.click()}
              className={`flex w-full flex-col items-center justify-center rounded-xl border border-dashed p-6 text-center transition-colors ${
                dragOver
                  ? "border-primary bg-primary/10"
                  : "border-border bg-muted hover:border-foreground/30"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) processImageFile(file);
                }}
              />
              <div className="mb-2 grid size-10 place-items-center rounded-full bg-secondary text-muted-foreground">
                <Upload size={18} strokeWidth={1.8} />
              </div>
              <p className="text-[12.5px] font-medium text-muted-foreground">
                <Trans>Drag, drop, or paste an image</Trans>
              </p>
              <span className="mt-3 rounded-lg bg-secondary px-3 py-1.5 text-xs font-medium text-foreground">
                <Trans>Choose file</Trans>
              </span>
            </button>
          )}

          <DialogFooter className="border-border sm:justify-end">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex items-center gap-1.5 rounded-xl bg-secondary px-4 py-1.5 text-[13px] font-medium text-foreground transition-colors hover:bg-accent"
            >
              <Check size={14} />
              <Trans>Done</Trans>
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
